<template>
  <v-dialog v-model="opened" max-width="800" persistent>
    <v-form
      ref="form"
      v-model="valid"
      :readonly="!canEdit"
      @submit.prevent="save()"
    >
      <v-card :loading="loadingData" :disabled="loadingData">
        <v-card-title class="headline">
          {{ !id ? " Tambah" : "Ubah" }} Transfer Paket Member
          <v-btn icon @click="close()" class="ml-auto">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row justify="center" no-gutters>
              <v-col cols="12" md="10">
                <!-- Alert untuk status verified -->
                <v-alert
                  v-if="isStatusLocked"
                  type="info"
                  class="mb-4"
                  dense
                  outlined
                >
                  <v-icon slot="prepend">mdi-lock</v-icon>
                  Data sudah tersimpan dengan status verified. Semua field terkunci dan tidak dapat diubah lagi.
                </v-alert>
                
                <v-row dense>
                  <v-col cols="12" md="12">
                    <!-- Dari Member -->
                    <v-autocomplete
                      label="Dari Member"
                      dense
                      outlined
                      v-model="data.from_member_id"
                      :items="listMembers"
                      item-text="name"
                      item-value="id"
                      :rules="rules.from_member_id"
                      :readonly="isEdit || isStatusLocked"
                      :clearable="!isEdit && !isStatusLocked"
                      :menu-props="{ disabled: isEdit || isStatusLocked }"
                      :append-outer-icon="(isEdit || isStatusLocked) ? 'mdi-lock' : undefined"
                    />

                    <!-- Paket yang akan ditransfer -->
                    <!-- <v-autocomplete
                      label="Paket yang akan ditransfer"
                      dense
                      outlined
                      v-model="data.sales_item_id"
                      :items="listSalesItems"
                      item-text="name"
                      item-value="id"
                      :rules="rules.sales_item_id"
                      :readonly="isEdit"
                      :clearable="!isEdit"
                      :menu-props="{ disabled: isEdit }"
                      :append-outer-icon="isEdit ? 'mdi-lock' : undefined"
                    /> -->

                    <!-- Ke Member -->
                    <v-autocomplete
                      label="Ke Member"
                      dense
                      outlined
                      v-model="data.to_member_id"
                      :items="listMembers"
                      item-text="name"
                      item-value="id"
                      :rules="rules.to_member_id"
                      :readonly="isEdit || isStatusLocked"
                      :clearable="!isEdit && !isStatusLocked"
                      :menu-props="{ disabled: isEdit || isStatusLocked }"
                      :append-outer-icon="(isEdit || isStatusLocked) ? 'mdi-lock' : undefined"
                    />

                    <!-- Info Detail Paket Transfer -->
                    <v-card
                      v-if="transfer_request_details.length > 0"
                      outlined
                      class="mb-4"
                    >
                      <v-card-title class="subtitle-1 font-weight-bold">
                        <v-icon left color="info">mdi-information-outline</v-icon>
                        Detail Paket yang Ditransfer
                      </v-card-title>
                      <v-card-text>
                        <v-simple-table dense>
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th class="text-left">Paket</th>
                                <th class="text-left">Tanggal Original Mulai Paket</th>
                                <th class="text-left">Tanggal Original Berakhir Paket</th>
                                <th class="text-right">Total Hari</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="detail in transfer_request_details"
                                :key="detail.id"
                              >
                                <td>{{ detail.sales_item?.package?.name || '-' }}</td>
                                <td>{{ detail.original_date_start || '-' }}</td>
                                <td>{{ detail.original_date_end || '-' }}</td>
                                <td class="text-right">{{ detail.total_days }} hari</td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </v-card-text>
                    </v-card>

                    <CurrencyInput
                      v-model="data.price"
                      placeholder="Masukkan harga"
                      :rules="[rules.price]"
                      :readonly="isStatusLocked"
                    />

                    <v-autocomplete
                      label="Metode Pembayaran"
                      dense
                      outlined
                      v-model="data.payment_method_id"
                      :items="listPaymentMethods"
                      item-text="name"
                      item-value="id"
                      :clearable="!isStatusLocked"
                      :rules="rules.payment_method_id"
                      :readonly="isStatusLocked"
                      :menu-props="{ disabled: isStatusLocked }"
                      :append-outer-icon="isStatusLocked ? 'mdi-lock' : undefined"
                    />
                    <v-autocomplete
                      label="Bank Tujuan"
                      dense
                      outlined
                      v-model="data.destination_bank_id"
                      :items="listBanks"
                      item-text="name"
                      item-value="id"
                      :clearable="!isStatusLocked"
                      :rules="rules.destination_bank_id"
                      :readonly="isStatusLocked"
                      :menu-props="{ disabled: isStatusLocked }"
                      :append-outer-icon="isStatusLocked ? 'mdi-lock' : undefined"
                    />
                    <v-text-field
                      label="No. Rekening"
                      dense
                      outlined
                      v-model="data.account_number"
                      :rules="rules.account_number"
                      :readonly="isStatusLocked"
                      :append-outer-icon="isStatusLocked ? 'mdi-lock' : undefined"
                    />
                    <v-text-field
                      label="Nama Rekening"
                      dense
                      outlined
                      v-model="data.account_name"
                      :rules="rules.account_name"
                      :readonly="isStatusLocked"
                      :append-outer-icon="isStatusLocked ? 'mdi-lock' : undefined"
                    />

                    <v-col cols="12" md="12">
                      <div class="ml-3">
                        Bukti Transfer
                        <v-icon v-if="isStatusLocked" class="ml-2" small>mdi-lock</v-icon>
                      </div>
                      <FileUpload
                        v-model="data.transfer_evidence_id"
                        :labelIdle="isStatusLocked ? 'File terkunci (status sudah verified)' : 'Unggah Bukti Transfer'"
                        category="file"
                        class="mb-6"
                        :key="data.transfer_evidence_id"
                        :disabled="isStatusLocked"
                      />
                    </v-col>

                    <v-select
                      v-if="canVerification"
                      label="Status"
                      dense
                      outlined
                      v-model="data.status"
                      :items="listStatus"
                      item-text="name"
                      item-value="id"
                      clearable
                      :rules="[rules.status]"
                      :readonly="isStatusLocked"
                      :append-outer-icon="isStatusLocked ? 'mdi-lock' : undefined"
                    />

                    <v-textarea
                      label="Keterangan"
                      dense
                      outlined
                      v-model="data.description"
                      :readonly="isStatusLocked"
                      :append-outer-icon="isStatusLocked ? 'mdi-lock' : undefined"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-row no-gutters>
            <v-col cols="12" align="end">
              <v-btn
                depressed
                class="ma-1"
                :disabled="loading"
                @click="close()"
              >
                <v-icon small left>mdi-close</v-icon>
                Tutup
              </v-btn>
              <v-btn
                v-if="canEdit"
                type="submit"
                color="success"
                depressed
                class="ma-1"
                :loading="loading"
                :disabled="loading || isStatusLocked"
                @click.prevent="save()"
              >
                <v-icon small left>mdi-content-save</v-icon>
                Simpan
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { slugify } from "@/core/utils";
import UploadAdapter from "../../../../core/plugins/UploadAdapter";
import FileUpload from "@/view/component/FileUpload.vue";

export default {
  async mounted() {
    const members = await ApiService.get("user/list?role_slug=member");
    this.listMembers = members.data;

    const paymentMethods = await ApiService.get("payment-method/list");
    this.listPaymentMethods = paymentMethods.data;

    const banks = await ApiService.get("bank/list");
    this.listBanks = banks.data;
  },
  data() {
    return {
      action: "create",
      id: null,               // UUID saat edit
      opened: false,
      loading: false,
      loadingData: false,
      valid: true,
      originalStatus: null,   // Status awal saat data dimuat

      listMembers: [],
      listPaymentMethods: [],
      listBanks: [],
      listSalesItems: [],     // ⬅️ daftar paket
      listStatus: [
        { id: "unverified", name: "Unverified" },
        { id: "verified", name: "Verified" },
      ],
      transfer_request_details: [],

      data: {
        from_member_id: null,
        to_member_id: null,
        description: null,
        price: 0,
        payment_method_id: null,
        destination_bank_id: null,
        account_number: null,
        account_name: null,
        status: "unverified",
        transfer_evidence_id: null,
        sales_item_id: null,
      },

      rules: {
        from_member_id: (v) =>
          (v !== null && v !== undefined && v !== "") || "Member harus dipilih",
        to_member_id: (v) =>
          (v !== null && v !== undefined && v !== "") || "Member harus dipilih",
        sales_item_id: (v) =>
          (v !== null && v !== undefined && v !== "") || "Paket harus dipilih",
        price: (v) =>
          (v !== null && v !== undefined && v !== "") ||
          "Biaya harus diisi",
        payment_method_id: (v) =>
          (v !== null && v !== undefined && v !== "") ||
          "Metode pembayaran harus dipilih",
        destination_bank_id: (v) =>
          (v !== null && v !== undefined && v !== "") ||
          "Bank tujuan harus dipilih",
        account_number: (v) =>
          (v !== null && v !== undefined && v !== "") ||
          "No. rekening harus diisi",
        account_name: (v) =>
          (v !== null && v !== undefined && v !== "") ||
          "Nama rekening harus diisi",
        status: (v) =>
          (v !== null && v !== undefined && v !== "") || "Status harus dipilih",
      },
    };
  },
  props: {
    module: { type: String },
    success: { type: Function, default: function () {} },
  },
  methods: {
    open(id) {
      this.opened = true;
      setTimeout(() => {
        this.$refs.form.reset();
        if (id) {
          this.getData(id);
        } else {
          // Reset original status untuk create baru
          this.originalStatus = null;
        }
      }, 1);
    },

    async getData(id) {
      this.loadingData = true;
      this.id = id;
      try {
        const { data } = await ApiService.get("transfer-request?id=" + id);

        // Simpan status awal untuk referensi lock
        this.originalStatus = data.status;

        // Simpan transfer request details
        this.transfer_request_details = data.transfer_request_details || [];

        // Isi data awal (tahan from_member_id agar watcher tidak men-trigger fetch duluan)
        this.data = {
          slug: data.slug,
          price: data.price,
          sales_item_id: data.sales_item_id,
          description: data.description,
          from_member_id: null, // ditetapkan belakangan
          to_member_id: data.to_member_id,
          payment_method_id: data.payment_method_id,
          destination_bank_id: data.destination_bank_id,
          account_number: data.account_number,
          account_name: data.account_name,
          status: data.status,
          transfer_evidence_id: data.transfer_evidence_id,
        };

        // Saat EDIT: ambil paket dari endpoint non-active (semua item)
        await this.fetchListSalesItem(data.from_member_id);

        // Pastikan item terdaftar (fallback jika API tidak mengembalikan item-nya)
        const found = this.listSalesItems.find(
          (item) => item.id === data.sales_item_id
        );
        if (!found && data.sales_item?.package) {
          this.listSalesItems.push({
            id: data.sales_item.id,
            name: `${data.sales_item.package.name} | Expired Date: ${data.sales_item.expired_date}`,
          });
        }

        // Set nilai after fetching list
        this.data.from_member_id = data.from_member_id;

        // Paksa re-render sales_item_id bila perlu
        this.$nextTick(() => {
          this.data.sales_item_id = data.sales_item_id;
        });
      } catch (err) {
        if (err.response?.data) {
          this.handleError(err.response.data);
        }
      } finally {
        this.loadingData = false;
      }
    },

    async save() {
      this.$refs.form.validate();
      if (this.valid) {
        this.loading = true;
        const action = !this.id ? "post" : "update";
        let url = "transfer-request";
        if (this.id) {
          url = url + "?id=" + this.id;
        }
        ApiService[action](url, this.data)
          .then(({ data }) => {
            if (data.id) {
              this.loading = false;
              this.success(data);
              this.$swal.fire({
                icon: "success",
                title: "Berhasil",
                text: "Data berhasil disimpan",
                showConfirmButton: false,
                timer: 3000,
              });
              this.close();
            }
          })
          .catch(({ response }) => {
            if (response?.data) {
              this.handleError(response.data);
            }
            this.loading = false;
          });
      }
    },

    handleError(data) {
      if (data?.error) {
        this.$swal.fire({
          icon: "warning",
          title: "Terjadi kesalahan",
          text: data.error,
          showConfirmButton: true,
          timer: 5000,
        });
      }
    },

    close() {
      this.opened = false;
      this.data = {};
      this.id = null;
      this.originalStatus = null;
      this.transfer_request_details = [];
      this.$refs.form.reset();
      this.success();
    },

    uploader(editor) {
      editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
        return new UploadAdapter(loader, "file");
      };
    },

    // CREATE: hanya item aktif
    // async fetchListSalesItemActive(memberId) {
    //   this.loadingData = true;
    //   try {
    //     const response = await ApiService.get(
    //       `sales/list-item-active?member_id=${memberId}`
    //     );
    //     const dataSales = response.data || [];
    //     this.listSalesItems = dataSales.map((item) => ({
    //       id: item.id,
    //       name: `${item.package.name} | Expired Date: ${item.expired_date}`,
    //     }));
    //   } catch (error) {
    //     this.handleError(error.response?.data || { error: "Gagal memuat paket aktif" });
    //   } finally {
    //     this.loadingData = false;
    //   }
    // },

    // EDIT: semua item (termasuk non-active/expired)
    async fetchListSalesItem(memberId) {
      this.loadingData = true;
      try {
        const response = await ApiService.get(
          `sales/list-item?member_id=${memberId}`
        );
        const dataSales = response.data || [];
        this.listSalesItems = dataSales.map((item) => ({
          id: item.id,
          name: `${item.package.name} | Expired Date: ${item.expired_date}`,
        }));
      } catch (error) {
        this.handleError(error.response?.data || { error: "Gagal memuat paket" });
      } finally {
        this.loadingData = false;
      }
    },
  },

  watch: {
    ["data.name"](value) {
      this.data.slug = value ? slugify(value) : null;
    },
    // Dinamis: create vs edit untuk sumber paket
    ["data.from_member_id"](memberId) {
      if (memberId) {
        if (this.isEdit) {
          // this.fetchListSalesItem(memberId);        // EDIT
        } else {
          // this.fetchListSalesItemActive(memberId);  // CREATE
        }
      } else {
        this.listSalesItems = [];
      }
    },
  },

  components: {
    FileUpload,
  },

  computed: {
    // Aman untuk UUID string
    isEdit() {
      return Boolean(this.id);
    },
    canEdit() {
      if (this.id && !this.$store.getters.hasPermission(this.module, "update")) {
        return false;
      }
      return true;
    },
    canVerification() {
      return this.$store.getters.hasPermission(this.module, "verification");
    },
    isStatusLocked() {
      // Gembok semua field jika data sudah tersimpan (edit mode) dan status awal sudah verified
      return this.isEdit && this.originalStatus === "verified";
    },
  },
};
</script>
